import { useState, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Single condition evaluator - used by all visibility/required logic.
 * @param {Object} conditionGroup - The condition group with all/any arrays
 * @param {Function} getValue - (fieldId) => value
 * @param {Function} getRepeatCount - (fieldId) => number (for repeating sections)
 * @returns {boolean}
 */
const evaluateCondition = (
  conditionGroup,
  getValue,
  getRepeatCount = () => 0,
) => {
  if (!conditionGroup) return false;

  const checkSingle = (cond) => {
    // Handle repeat_count operators
    if (cond.operator?.startsWith('repeat_count_')) {
      const count = getRepeatCount(cond.field_id);
      const target = Number(cond.value);
      switch (cond.operator) {
        case 'repeat_count_equals':
          return count === target;
        case 'repeat_count_gt':
          return count > target;
        case 'repeat_count_gte':
          return count >= target;
        case 'repeat_count_lt':
          return count < target;
        case 'repeat_count_lte':
          return count <= target;
        default:
          return true;
      }
    }

    // Regular field value operators
    const value = getValue(cond.field_id);
    switch (cond.operator) {
      case 'equals':
        return value === cond.value;
      case 'not_equals':
        return value !== cond.value;
      case 'contains':
        return Array.isArray(value) && value.includes(cond.value);
      case 'not_contains':
        return !Array.isArray(value) || !value.includes(cond.value);
      case 'exists':
        return value !== undefined && value !== null && value !== '';
      case 'not_exists':
        return value === undefined || value === null || value === '';
      case 'in':
        return Array.isArray(cond.value) && cond.value.includes(value);
      case 'not_in':
        return !Array.isArray(cond.value) || !cond.value.includes(value);
      case 'gt':
        return Number(value) > Number(cond.value);
      case 'gte':
        return Number(value) >= Number(cond.value);
      case 'lt':
        return Number(value) < Number(cond.value);
      case 'lte':
        return Number(value) <= Number(cond.value);
      default:
        return true;
    }
  };

  const evalGroup = (group) => {
    if (!group) return true;
    const allPass =
      !group.all ||
      group.all.every((c) => (c.all || c.any ? evalGroup(c) : checkSingle(c)));
    const anyPass =
      !group.any ||
      group.any.some((c) => (c.all || c.any ? evalGroup(c) : checkSingle(c)));
    return allPass && anyPass;
  };

  return evalGroup(conditionGroup);
};

/**
 * Shared hook for DBQ form state management and helper functions.
 * Used by both DbqFormRenderer (admin preview) and UserDbqEditor (user submission).
 */
export const useDbqForm = (initialFormData = {}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [repeatingInstances, setRepeatingInstances] = useState({});
  const instanceCounterRef = useRef(0);
  const formJsonRef = useRef(null);

  // Set the form JSON reference (call this when formJson is available)
  const setFormJson = useCallback((formJson) => {
    formJsonRef.current = formJson;
  }, []);

  // Comments are stored in formData under the reserved key '_comments'
  const COMMENTS_KEY = '_comments';
  const SUGGESTIONS_KEY = '_suggestions';

  // Helper to generate unique instance IDs
  const generateInstanceId = useCallback((elementId) => {
    instanceCounterRef.current += 1;
    return `${elementId}_instance_${instanceCounterRef.current}_${Date.now()}`;
  }, []);

  // Build field key with instance path
  const buildFieldKey = useCallback((fieldId, instancePath = []) => {
    if (!fieldId) return '';
    return instancePath.length
      ? `${instancePath.join('__')}__${fieldId}`
      : fieldId;
  }, []);

  // Get field value considering instance path
  const getFieldValue = useCallback(
    (fieldId, instancePath = []) => {
      if (!fieldId) return undefined;
      const key = buildFieldKey(fieldId, instancePath);
      return formData[key] !== undefined ? formData[key] : formData[fieldId];
    },
    [formData, buildFieldKey],
  );

  // Check if a field is visible based on conditions
  const isVisible = useCallback(
    (visibleIf, instancePath = []) => {
      if (!visibleIf) return true;
      return evaluateCondition(
        visibleIf,
        (fieldId) => getFieldValue(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [repeatingInstances, getFieldValue],
  );

  // Check if a field is conditionally required based on required_if
  const isConditionallyRequired = useCallback(
    (field, instancePath = []) => {
      if (!field?.required_if) return false;
      return evaluateCondition(
        field.required_if,
        (fieldId) => getFieldValue(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [repeatingInstances, getFieldValue],
  );

  // Check if an option is conditionally required based on required_if
  const isOptionConditionallyRequired = useCallback(
    (option, instancePath = []) => {
      if (!option?.required_if) return false;
      return evaluateCondition(
        option.required_if,
        (fieldId) => getFieldValue(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [repeatingInstances, getFieldValue],
  );

  // Helper to recursively collect all fields from the schema with their visible_if conditions
  const collectAllFields = useCallback((elements, collected = []) => {
    if (!elements) return collected;

    for (const element of elements) {
      if (element.type === 'field' && element.id) {
        collected.push({ id: element.id, visible_if: element.visible_if });
      }
      // Recurse into nested elements (groups, sections)
      if (element.elements) {
        collectAllFields(element.elements, collected);
      }
      // Recurse into options' child_elements
      if (element.options) {
        for (const option of element.options) {
          if (option.child_elements) {
            collectAllFields(option.child_elements, collected);
          }
        }
      }
    }
    return collected;
  }, []);

  // Check if a visible_if condition references a specific field ID
  const conditionReferencesField = useCallback((visibleIf, targetFieldId) => {
    if (!visibleIf) return false;

    const checkConditions = (conditions) => {
      if (!conditions) return false;
      return conditions.some((cond) => {
        // Check if it's a nested condition group
        if (cond.all || cond.any) {
          return (
            (cond.all && checkConditions(cond.all)) ||
            (cond.any && checkConditions(cond.any))
          );
        }
        // It's a simple condition
        return cond.field_id === targetFieldId;
      });
    };

    return (
      (visibleIf.all && checkConditions(visibleIf.all)) ||
      (visibleIf.any && checkConditions(visibleIf.any))
    );
  }, []);

  // Collect all fields and options with required_if that have notify: true
  const collectNotifiableRequiredIfs = useCallback((formJson) => {
    if (!formJson?.sections) return [];

    const result = []; // { fieldId, fieldLabel, optionId?, optionLabel?, required_if }

    const traverseElements = (elements) => {
      if (!Array.isArray(elements)) return;

      for (const element of elements) {
        if (!element) continue;

        if (element.type === 'field') {
          // Field-level required_if with notify
          if (element.required_if?.notify) {
            result.push({
              fieldId: element.id,
              fieldLabel: element.label || element.id,
              required_if: element.required_if,
              fieldType: element.field_type,
            });
          }

          // Option-level required_if with notify
          if (element.options) {
            for (const option of element.options) {
              if (option.required_if?.notify) {
                result.push({
                  fieldId: element.id,
                  fieldLabel: element.label || element.id,
                  optionId: option.id,
                  optionLabel: option.label || option.id,
                  required_if: option.required_if,
                  fieldType: element.field_type,
                });
              }

              // Recurse into child_elements
              if (option.child_elements) {
                traverseElements(option.child_elements);
              }
            }
          }
        }

        // Recurse into groups
        if (element.elements) {
          traverseElements(element.elements);
        }
      }
    };

    for (const section of formJson.sections) {
      if (section.elements) {
        traverseElements(section.elements);
      }
    }

    return result;
  }, []);

  // Helper to create getValue function for specific form data
  const createGetValueForData = useCallback((data, instancePath = []) => {
    return (fieldId) => {
      const key = instancePath.length
        ? `${instancePath.join('__')}__${fieldId}`
        : fieldId;
      return data[key] !== undefined ? data[key] : data[fieldId];
    };
  }, []);

  // Helper to find a field element by ID in the form schema
  const findFieldElement = useCallback((formJson, targetFieldId) => {
    if (!formJson?.sections) return null;

    const search = (elements) => {
      if (!Array.isArray(elements)) return null;
      for (const element of elements) {
        if (!element) continue;
        if (element.type === 'field' && element.id === targetFieldId) {
          return element;
        }
        if (element.elements) {
          const found = search(element.elements);
          if (found) return found;
        }
        if (element.options) {
          for (const option of element.options) {
            if (option.child_elements) {
              const found = search(option.child_elements);
              if (found) return found;
            }
          }
        }
      }
      return null;
    };

    for (const section of formJson.sections) {
      if (section.elements) {
        const found = search(section.elements);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Helper to recursively collect all field IDs from child_elements
  const collectChildFieldIds = useCallback((childElements, collected = []) => {
    if (!Array.isArray(childElements)) return collected;
    for (const element of childElements) {
      if (!element) continue;
      if (element.type === 'field' && element.id) {
        collected.push(element.id);
      }
      if (element.elements) {
        collectChildFieldIds(element.elements, collected);
      }
      if (element.options) {
        for (const option of element.options) {
          if (option.child_elements) {
            collectChildFieldIds(option.child_elements, collected);
          }
        }
      }
    }
    return collected;
  }, []);

  // Handle field value changes - returns { newlyRequired } for caller to handle
  const handleFieldChange = useCallback(
    (fieldId, value, instancePath = []) => {
      const key = buildFieldKey(fieldId, instancePath);
      const prev = formData;
      const newFormData = { ...prev, [key]: value };
      const newlyRequired = [];

      // Helper to get value from new form data
      const getValueFromNew = (fId) => {
        const k = instancePath.length
          ? `${instancePath.join('__')}__${fId}`
          : fId;
        return newFormData[k] !== undefined ? newFormData[k] : newFormData[fId];
      };

      // Helper to get repeat count
      const getRepeatCount = (fId) => (repeatingInstances[fId] || []).length;

      // If we have formJson, handle clearing of child_elements and visible_if dependents
      if (formJsonRef.current?.sections) {
        const fieldElement = findFieldElement(formJsonRef.current, fieldId);

        // Clear child_elements when options are unchecked/deselected
        if (fieldElement?.options) {
          const oldValue = prev[key];
          const fieldType = fieldElement.field_type;

          // Determine which options were removed
          let removedOptionIds = [];

          if (fieldType === 'multi_select') {
            const oldArr = Array.isArray(oldValue) ? oldValue : [];
            const newArr = Array.isArray(value) ? value : [];
            removedOptionIds = oldArr.filter(
              (optId) => !newArr.includes(optId),
            );
          } else if (fieldType === 'single_select') {
            // If old value had an option and new value is different, old is removed
            if (oldValue && oldValue !== value) {
              removedOptionIds = [oldValue];
            }
          } else if (fieldType === 'yes_no') {
            // For yes_no, switching from true to false removes 'yes' option's children
            // and switching from false to true removes 'no' option's children
            if (oldValue === true && value === false) {
              removedOptionIds = ['yes'];
            } else if (oldValue === false && value === true) {
              removedOptionIds = ['no'];
            }
          }

          // For each removed option, clear its child_elements. Cleared as null,
          // NOT undefined: undefined keys vanish from the JSON save payload, so
          // the server would silently keep the old value (and, post-sign, its
          // "Edited after signing" stamp) while the UI showed blank.
          for (const removedOptId of removedOptionIds) {
            const option = fieldElement.options.find(
              (o) => o.id === removedOptId,
            );
            if (option?.child_elements) {
              const childFieldIds = collectChildFieldIds(option.child_elements);
              for (const childId of childFieldIds) {
                const childKey = buildFieldKey(childId, instancePath);
                if (
                  newFormData[childKey] !== undefined &&
                  newFormData[childKey] !== null
                ) {
                  newFormData[childKey] = null;
                }
              }
            }
          }
        }
        // Collect all fields from the schema
        const allFields = [];
        for (const section of formJsonRef.current.sections) {
          collectAllFields(section.elements, allFields);
        }

        // Find fields with visible_if that reference the changed field
        const dependentFields = allFields.filter(
          (f) =>
            f.visible_if && conditionReferencesField(f.visible_if, fieldId),
        );

        // For each dependent field, check if its condition is now false
        for (const depField of dependentFields) {
          const depKey = buildFieldKey(depField.id, instancePath);
          const isNowVisible = evaluateCondition(
            depField.visible_if,
            getValueFromNew,
            getRepeatCount,
          );

          // If the field is now hidden, clear its value (null, not undefined -
          // see the child_elements clear above for why).
          if (
            !isNowVisible &&
            newFormData[depKey] !== undefined &&
            newFormData[depKey] !== null
          ) {
            newFormData[depKey] = null;
          }
        }

        // Detect fields/options with required_if (notify:true) that just became required
        const notifiables = collectNotifiableRequiredIfs(formJsonRef.current);

        for (const item of notifiables) {
          const getValueFromPrev = createGetValueForData(prev, instancePath);
          const wasRequired = evaluateCondition(
            item.required_if,
            getValueFromPrev,
            getRepeatCount,
          );
          const isNowRequired = evaluateCondition(
            item.required_if,
            getValueFromNew,
            getRepeatCount,
          );

          // Only notify if transitioning from not-required to required
          if (!wasRequired && isNowRequired) {
            // Check if the field/option is already filled
            const fieldKey = buildFieldKey(item.fieldId, instancePath);
            const fieldValue =
              newFormData[fieldKey] ?? newFormData[item.fieldId];

            let isFilled = false;
            if (item.optionId) {
              // Option-level: check if the option is selected
              if (item.fieldType === 'multi_select') {
                isFilled =
                  Array.isArray(fieldValue) &&
                  fieldValue.includes(item.optionId);
              } else if (item.fieldType === 'yes_no') {
                isFilled = fieldValue === (item.optionId === 'yes');
              } else {
                isFilled = fieldValue === item.optionId;
              }
            } else {
              // Field-level: check if field has a value
              if (item.fieldType === 'multi_select') {
                isFilled = Array.isArray(fieldValue) && fieldValue.length > 0;
              } else if (typeof fieldValue === 'boolean') {
                isFilled = true; // yes/no always has a value once set
              } else {
                isFilled =
                  fieldValue !== undefined &&
                  fieldValue !== null &&
                  fieldValue !== '';
              }
            }

            // Only notify if not filled
            if (!isFilled) {
              newlyRequired.push({
                fieldId: item.fieldId,
                fieldLabel: item.fieldLabel,
                optionId: item.optionId,
                optionLabel: item.optionLabel,
              });
            }
          }
        }
      }

      setFormData(newFormData);
      return { newlyRequired };
    },
    [
      formData,
      buildFieldKey,
      repeatingInstances,
      collectAllFields,
      conditionReferencesField,
      collectNotifiableRequiredIfs,
      createGetValueForData,
      findFieldElement,
      collectChildFieldIds,
    ],
  );

  // Add a new instance to a repeating element
  const handleAddRepeatingInstance = useCallback(
    (element) => {
      if (!element?.id) return;
      const maxItems = parseInt(element.max_items) || 0;

      setRepeatingInstances((prev) => {
        const currentInstances = prev[element.id] || [];
        // Check if we've reached max (0 means unlimited)
        if (maxItems > 0 && currentInstances.length >= maxItems) {
          return prev;
        }

        const newInstanceId = generateInstanceId(element.id);
        return {
          ...prev,
          [element.id]: [...currentInstances, newInstanceId],
        };
      });
    },
    [generateInstanceId],
  );

  // Remove an instance from a repeating element
  const handleRemoveRepeatingInstance = useCallback((element, instanceId) => {
    if (!element?.id || !instanceId) return;
    const minItems = Math.max(parseInt(element.min_items) || 1, 1);

    setRepeatingInstances((prev) => {
      const currentInstances = prev[element.id] || [];
      // Can't remove if we're at minimum
      if (currentInstances.length <= minItems) {
        return prev;
      }

      return {
        ...prev,
        [element.id]: currentInstances.filter((id) => id !== instanceId),
      };
    });

    // Clear form data for this instance
    setFormData((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.includes(instanceId)) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  /**
   * Extract repeating instance IDs from formData keys.
   * Used to restore saved instance IDs on page reload.
   * Deduplicates by instance number, keeping only the latest timestamp for each.
   * @param {Object} data - The formData object
   * @returns {Object} Map of elementId -> array of instance IDs
   */
  const extractInstanceIdsFromFormData = useCallback((data) => {
    if (!data) return {};

    const repeatingFieldKeys = new Set(Object.keys(data));
    const suggestionsByField = data._suggestions;
    if (
      suggestionsByField &&
      typeof suggestionsByField === 'object' &&
      !Array.isArray(suggestionsByField)
    ) {
      Object.entries(suggestionsByField).forEach(([fieldKey, suggestions]) => {
        if (!Array.isArray(suggestions)) return;

        const hasVisibleSuggestion = suggestions.some((suggestion) => {
          const status = String(suggestion?.status || 'pending').toLowerCase();
          return status !== 'dismissed';
        });
        if (!hasVisibleSuggestion) return;

        repeatingFieldKeys.add(fieldKey);
        suggestions.forEach((suggestion) => {
          if (suggestion?.fieldId)
            repeatingFieldKeys.add(String(suggestion.fieldId));
        });
      });
    }

    // First pass: collect all instances with their timestamps
    // Structure: { elementId: { instanceNum: { fullId, timestamp } } }
    const instanceMap = {};

    repeatingFieldKeys.forEach((key) => {
      // Match pattern: {elementId}_instance_{num}_{timestamp}__{fieldName}
      const match = key.match(/^(.+)_instance_(\d+)_(\d+)__/);
      if (match) {
        const [, elementId, instanceNum, timestamp] = match;
        const fullInstanceId = `${elementId}_instance_${instanceNum}_${timestamp}`;
        const ts = parseInt(timestamp, 10);

        if (!instanceMap[elementId]) {
          instanceMap[elementId] = {};
        }

        // Keep only the latest timestamp for each instance number
        const existing = instanceMap[elementId][instanceNum];
        if (!existing || ts > existing.timestamp) {
          instanceMap[elementId][instanceNum] = {
            fullId: fullInstanceId,
            timestamp: ts,
          };
        }
      }
    });

    // Second pass: convert to sorted arrays (by instance number)
    // Also track the maximum instance number to update the counter
    let maxInstanceNum = 0;
    const result = {};
    Object.entries(instanceMap).forEach(([elementId, instanceNumMap]) => {
      const instances = Object.entries(instanceNumMap)
        .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10)) // Sort by instance number
        .map(([numStr, { fullId }]) => {
          const num = parseInt(numStr, 10);
          if (num > maxInstanceNum) maxInstanceNum = num;
          return fullId;
        });
      result[elementId] = instances;
    });

    // Update the counter to avoid collisions when adding new instances
    if (maxInstanceNum > instanceCounterRef.current) {
      instanceCounterRef.current = maxInstanceNum;
    }

    return result;
  }, []);

  // Initialize repeating instances from form JSON
  // If existingFormData is provided, restore saved instance IDs instead of generating new ones
  const initializeRepeatingInstances = useCallback(
    (formJson, existingFormData = {}) => {
      if (!formJson) return;

      // Extract saved instance IDs from formData
      const savedInstances = extractInstanceIdsFromFormData(existingFormData);

      const newInstances = {};

      const traverseElements = (elements = []) => {
        if (!Array.isArray(elements)) return;

        elements.forEach((element) => {
          if (!element) return;

          // Check if this is a repeating element
          if (element.repeating) {
            const minItems = Math.max(parseInt(element.min_items) || 1, 1);

            // Check if we have saved instances for this element
            if (
              savedInstances[element.id] &&
              savedInstances[element.id].length > 0
            ) {
              // Restore saved instance IDs
              newInstances[element.id] = savedInstances[element.id];
            } else {
              // No saved data - create new instances
              newInstances[element.id] = Array.from({ length: minItems }, () =>
                generateInstanceId(element.id),
              );
            }
          }

          // Recursively check nested elements
          if (element.elements) {
            traverseElements(element.elements);
          }

          // Check options with child_elements
          if (element.options) {
            element.options.forEach((option) => {
              if (option.child_elements) {
                traverseElements(option.child_elements);
              }
            });
          }
        });
      };

      // Traverse all sections
      formJson.sections?.forEach((section) => {
        if (section.elements) {
          traverseElements(section.elements);
        }
      });

      setRepeatingInstances(newInstances);
    },
    [generateInstanceId, extractInstanceIdsFromFormData],
  );

  // Build a map of elements that should be rendered with specific groups
  const buildRenderWithGroupMap = useCallback((formJson) => {
    const map = {}; // { groupId: [elements] }

    const traverseElements = (elements = []) => {
      if (!Array.isArray(elements)) return;

      elements.forEach((element) => {
        if (!element) return;

        // If this element should render with a group, add it to the map
        if (element.render_with_group) {
          if (!map[element.render_with_group]) {
            map[element.render_with_group] = [];
          }
          map[element.render_with_group].push(element);
        }

        // Recursively check nested elements
        if (element.elements) {
          traverseElements(element.elements);
        }

        // Check options with child_elements
        if (element.options) {
          element.options.forEach((option) => {
            if (option.child_elements) {
              traverseElements(option.child_elements);
            }
          });
        }
      });
    };

    // Traverse all sections
    formJson?.sections?.forEach((section) => {
      if (section.elements) {
        traverseElements(section.elements);
      }
    });

    return map;
  }, []);

  // =====================
  // COMMENT MANAGEMENT
  // =====================

  const getArtifactEntries = useCallback(
    (artifactKey, fieldId) => {
      const artifactsByField = formData[artifactKey] || {};
      return artifactsByField[fieldId] || [];
    },
    [formData],
  );

  const getOrderedArtifactFieldIds = useCallback((fieldIds, formJson) => {
    const originalFieldIds = Array.from(fieldIds || []);
    const keysWithArtifacts = new Set(originalFieldIds);

    if (keysWithArtifacts.size === 0) return [];
    if (!formJson?.sections) return originalFieldIds;

    const bareIdToKeys = {};
    for (const key of keysWithArtifacts) {
      const bareId = key.includes('__') ? key.split('__').pop() : key;
      if (!bareIdToKeys[bareId]) bareIdToKeys[bareId] = [];
      bareIdToKeys[bareId].push(key);
    }

    const orderedKeys = [];
    const orderedKeySet = new Set();

    const traverseElements = (elements) => {
      if (!Array.isArray(elements)) return;

      for (const element of elements) {
        if (!element) continue;

        if (element.type === 'field' && bareIdToKeys[element.id]) {
          for (const key of bareIdToKeys[element.id]) {
            if (!orderedKeySet.has(key)) {
              orderedKeys.push(key);
              orderedKeySet.add(key);
            }
          }
        }

        if (element.elements) {
          traverseElements(element.elements);
        }

        if (element.options) {
          for (const option of element.options) {
            if (option.child_elements) {
              traverseElements(option.child_elements);
            }
          }
        }
      }
    };

    for (const section of formJson.sections) {
      if (section.elements) {
        traverseElements(section.elements);
      }
    }

    const unmatchedKeys = originalFieldIds.filter(
      (key) => !orderedKeySet.has(key),
    );
    return [...orderedKeys, ...unmatchedKeys];
  }, []);

  const getOrderedActiveArtifactFieldIds = useCallback(
    (artifactKey, isActive, formJson) => {
      const artifacts = formData[artifactKey] || {};

      const activeFieldIds = Object.entries(artifacts)
        .filter(
          ([, entries]) =>
            Array.isArray(entries) && entries.some((entry) => isActive(entry)),
        )
        .map(([key]) => key);

      return getOrderedArtifactFieldIds(activeFieldIds, formJson);
    },
    [formData, getOrderedArtifactFieldIds],
  );

  // Get all comments for a specific field (only unresolved)
  const getFieldComments = useCallback(
    (fieldId) => {
      const fieldComments = getArtifactEntries(COMMENTS_KEY, fieldId);
      return fieldComments.filter((comment) => !comment.resolved);
    },
    [getArtifactEntries],
  );

  // Get ALL comments including resolved (for internal use)
  const getAllFieldComments = useCallback(
    (fieldId) => {
      return getArtifactEntries(COMMENTS_KEY, fieldId);
    },
    [getArtifactEntries],
  );

  // Add a new comment to a field
  const addComment = useCallback((fieldId, author, text) => {
    if (!fieldId || !author || !text?.trim()) return;

    const newComment = {
      id: uuidv4(),
      author: { userId: author.userId, name: author.name },
      text: text.trim(),
      createdAt: new Date().toISOString(),
      resolved: false,
      replies: [],
    };

    setFormData((prev) => {
      const comments = prev[COMMENTS_KEY] || {};
      const fieldComments = comments[fieldId] || [];
      return {
        ...prev,
        [COMMENTS_KEY]: {
          ...comments,
          [fieldId]: [...fieldComments, newComment],
        },
      };
    });

    return newComment.id;
  }, []);

  // Add a reply to an existing comment
  const addReply = useCallback((fieldId, commentId, author, text) => {
    if (!fieldId || !commentId || !author || !text?.trim()) return;

    const newReply = {
      id: uuidv4(),
      author: { userId: author.userId, name: author.name },
      text: text.trim(),
      createdAt: new Date().toISOString(),
    };

    setFormData((prev) => {
      const comments = prev[COMMENTS_KEY] || {};
      const fieldComments = comments[fieldId] || [];

      const updatedFieldComments = fieldComments.map((comment) => {
        if (comment.id === commentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), newReply],
          };
        }
        return comment;
      });

      return {
        ...prev,
        [COMMENTS_KEY]: {
          ...comments,
          [fieldId]: updatedFieldComments,
        },
      };
    });

    return newReply.id;
  }, []);

  // Resolve a comment (hide from everyone)
  const resolveComment = useCallback((fieldId, commentId) => {
    if (!fieldId || !commentId) return;

    setFormData((prev) => {
      const comments = prev[COMMENTS_KEY] || {};
      const fieldComments = comments[fieldId] || [];

      const updatedFieldComments = fieldComments.map((comment) => {
        if (comment.id === commentId) {
          return { ...comment, resolved: true };
        }
        return comment;
      });

      return {
        ...prev,
        [COMMENTS_KEY]: {
          ...comments,
          [fieldId]: updatedFieldComments,
        },
      };
    });
  }, []);

  // Check if there are any unresolved comments
  const hasActiveComments = useCallback(() => {
    const comments = formData[COMMENTS_KEY] || {};
    return Object.values(comments).some((fieldComments) =>
      fieldComments.some((c) => !c.resolved),
    );
  }, [formData]);

  // Get ordered list of comment keys that have active (unresolved) comments
  // Orders by position in form if formJson is provided
  // Comment keys may be composite (instancePath__fieldId) for repeating fields
  const getAllActiveCommentFieldIds = useCallback(
    (formJson) => {
      return getOrderedActiveArtifactFieldIds(
        COMMENTS_KEY,
        (comment) => !comment.resolved,
        formJson,
      );
    },
    [getOrderedActiveArtifactFieldIds],
  );

  // Get count of unresolved comments for a field
  const getFieldCommentCount = useCallback(
    (fieldId) => {
      const fieldComments = getArtifactEntries(COMMENTS_KEY, fieldId);
      return fieldComments.filter((c) => !c.resolved).length;
    },
    [getArtifactEntries],
  );

  // Get total count of unresolved comments across all fields
  const getTotalCommentCount = useCallback(() => {
    const comments = formData[COMMENTS_KEY] || {};
    return Object.values(comments).reduce((total, fieldComments) => {
      return total + fieldComments.filter((c) => !c.resolved).length;
    }, 0);
  }, [formData]);

  // =====================
  // SUGGESTION MANAGEMENT
  // =====================

  const getFieldSuggestions = useCallback(
    (fieldId) => {
      return getArtifactEntries(SUGGESTIONS_KEY, fieldId).filter(
        (suggestion) => suggestion?.status !== 'dismissed',
      );
    },
    [getArtifactEntries],
  );

  const buildSuggestionStats = useCallback((suggestions = []) => {
    return suggestions.reduce(
      (stats, suggestion) => {
        const status = String(suggestion?.status || 'pending').toLowerCase();

        stats.total += 1;
        if (status === 'accepted') stats.accepted += 1;
        else if (status === 'rejected') stats.rejected += 1;
        else if (status === 'dismissed') stats.dismissed += 1;
        else stats.pending += 1;

        return stats;
      },
      {
        total: 0,
        pending: 0,
        accepted: 0,
        rejected: 0,
        dismissed: 0,
      },
    );
  }, []);

  const getActiveFieldSuggestions = useCallback(
    (fieldId) => {
      const suggestions = getArtifactEntries(SUGGESTIONS_KEY, fieldId);
      return suggestions.filter(
        (suggestion) => suggestion?.status === 'pending',
      );
    },
    [getArtifactEntries],
  );

  const getAllSuggestionFieldIds = useCallback(
    (formJson) => {
      const suggestions = formData[SUGGESTIONS_KEY] || {};
      const fieldIds = Object.entries(suggestions)
        .filter(([, entries]) => Array.isArray(entries) && entries.length > 0)
        .map(([key]) => key);

      return getOrderedArtifactFieldIds(fieldIds, formJson);
    },
    [formData, getOrderedArtifactFieldIds],
  );

  const getAllActiveSuggestionFieldIds = useCallback(
    (formJson) => {
      return getOrderedActiveArtifactFieldIds(
        SUGGESTIONS_KEY,
        (suggestion) => suggestion?.status === 'pending',
        formJson,
      );
    },
    [getOrderedActiveArtifactFieldIds],
  );

  const getAllReviewFieldIds = useCallback(
    (formJson) => {
      const comments = formData[COMMENTS_KEY] || {};
      const suggestions = formData[SUGGESTIONS_KEY] || {};

      const reviewFieldIds = new Set([
        ...Object.entries(comments)
          .filter(
            ([, entries]) =>
              Array.isArray(entries) &&
              entries.some((comment) => !comment.resolved),
          )
          .map(([key]) => key),
        ...Object.entries(suggestions)
          .filter(([, entries]) => Array.isArray(entries) && entries.length > 0)
          .map(([key]) => key),
      ]);

      return getOrderedArtifactFieldIds(reviewFieldIds, formJson);
    },
    [formData, getOrderedArtifactFieldIds],
  );

  const getAllActiveReviewFieldIds = useCallback(
    (formJson) => {
      const comments = formData[COMMENTS_KEY] || {};
      const suggestions = formData[SUGGESTIONS_KEY] || {};

      const activeFieldIds = new Set([
        ...Object.entries(comments)
          .filter(
            ([, entries]) =>
              Array.isArray(entries) &&
              entries.some((comment) => !comment.resolved),
          )
          .map(([key]) => key),
        ...Object.entries(suggestions)
          .filter(
            ([, entries]) =>
              Array.isArray(entries) &&
              entries.some((suggestion) => suggestion?.status === 'pending'),
          )
          .map(([key]) => key),
      ]);

      return getOrderedArtifactFieldIds(activeFieldIds, formJson);
    },
    [formData, getOrderedArtifactFieldIds],
  );

  const getFieldSuggestionStats = useCallback(
    (fieldId) => {
      return buildSuggestionStats(getArtifactEntries(SUGGESTIONS_KEY, fieldId));
    },
    [buildSuggestionStats, getArtifactEntries],
  );

  const getFieldSuggestionCount = useCallback(
    (fieldId) => {
      return getFieldSuggestionStats(fieldId).total;
    },
    [getFieldSuggestionStats],
  );

  const getTotalSuggestionStats = useCallback(() => {
    const suggestions = formData[SUGGESTIONS_KEY] || {};
    return buildSuggestionStats(
      Object.values(suggestions).flatMap((entries) =>
        Array.isArray(entries) ? entries : [],
      ),
    );
  }, [buildSuggestionStats, formData]);

  const getTotalSuggestionCount = useCallback(() => {
    return getTotalSuggestionStats().total;
  }, [getTotalSuggestionStats]);

  // =====================
  // REQUIRED FIELD VALIDATION
  // =====================

  /**
   * Check if a field value is considered "filled" (not empty)
   */
  const isFieldFilled = useCallback((fieldValue, fieldType) => {
    if (fieldValue === undefined || fieldValue === null) return false;
    if (fieldType === 'multi_select') {
      return Array.isArray(fieldValue) && fieldValue.length > 0;
    }
    if (typeof fieldValue === 'boolean') return true; // yes/no fields
    if (typeof fieldValue === 'string') return fieldValue.trim() !== '';
    if (typeof fieldValue === 'number') return true;
    return false;
  }, []);

  /**
   * Get all field elements from the form schema
   */
  const getAllFieldElements = useCallback((formJson) => {
    const fields = [];

    const traverseElements = (
      elements,
      parentPath = [],
      sectionLabel = '',
      parentOptionChain = [],
      parentVisibleIfs = [],
    ) => {
      if (!Array.isArray(elements)) return;

      for (const element of elements) {
        if (!element) continue;

        if (element.type === 'field') {
          fields.push({
            ...element,
            parentPath,
            sectionLabel,
            parentOptionChain,
            parentVisibleIfs,
          });
        }

        if (element.elements) {
          // If this element (e.g. a group) has a visible_if, propagate it to children
          const nextVisibleIfs = element.visible_if
            ? [...parentVisibleIfs, element.visible_if]
            : parentVisibleIfs;
          traverseElements(
            element.elements,
            parentPath,
            sectionLabel,
            parentOptionChain,
            nextVisibleIfs,
          );
        }

        if (element.options) {
          for (const option of element.options) {
            if (option.child_elements) {
              // Only track the chain for field elements (which control visibility)
              const nextChain =
                element.type === 'field'
                  ? [
                      ...parentOptionChain,
                      {
                        parentFieldId: element.id,
                        parentFieldType: element.field_type,
                        optionId: option.id,
                      },
                    ]
                  : parentOptionChain;
              traverseElements(
                option.child_elements,
                parentPath,
                sectionLabel,
                nextChain,
                parentVisibleIfs,
              );
            }
          }
        }
      }
    };

    formJson?.sections?.forEach((section) => {
      if (section.elements) {
        traverseElements(section.elements, [], section.label || '', [], []);
      }
    });

    return fields;
  }, []);

  /**
   * Get missing required fields that are currently visible
   * @param {Object} formJson - The form schema
   * @param {string[]} requiredFields - Array of field IDs marked as required by admin
   * @returns {Array<{id: string, label: string}>} List of missing required fields
   */
  const getMissingRequiredFields = useCallback(
    (formJson, requiredFields = []) => {
      if (!formJson) {
        return [];
      }

      const allFields = getAllFieldElements(formJson);
      const missingFields = [];

      for (const field of allFields) {
        // Check if the field itself is visible
        const fieldVisible = isVisible(field.visible_if, []);
        if (!fieldVisible) continue;

        // Check if all parent group visible_if conditions are met
        // Fields inside groups with visible_if are hidden when the group condition is false
        if (field.parentVisibleIfs && field.parentVisibleIfs.length > 0) {
          let allParentsVisible = true;
          for (const parentVisibleIf of field.parentVisibleIfs) {
            if (!isVisible(parentVisibleIf, [])) {
              allParentsVisible = false;
              break;
            }
          }
          if (!allParentsVisible) continue;
        }

        // Check if all parent options in the chain are selected
        // Fields inside option.child_elements are only visible when the parent option is selected
        if (field.parentOptionChain && field.parentOptionChain.length > 0) {
          let parentVisible = true;
          for (const chain of field.parentOptionChain) {
            const parentValue = getFieldValue(chain.parentFieldId, []);
            if (chain.parentFieldType === 'multi_select') {
              if (
                !Array.isArray(parentValue) ||
                !parentValue.includes(chain.optionId)
              ) {
                parentVisible = false;
                break;
              }
            } else if (chain.parentFieldType === 'yes_no') {
              const expected = chain.optionId === 'yes' ? true : false;
              if (parentValue !== expected) {
                parentVisible = false;
                break;
              }
            } else {
              // single_select or other
              if (parentValue !== chain.optionId) {
                parentVisible = false;
                break;
              }
            }
          }
          if (!parentVisible) continue;
        }

        // Field is required if:
        // 1. It has required: true in schema, OR
        // 2. It's in the admin-marked requiredFields list, OR
        // 3. It has required_if condition that evaluates to true
        const isSchemaRequired = field.required === true;
        const isAdminRequired =
          Array.isArray(requiredFields) && requiredFields.includes(field.id);
        const isConditionalRequired = isConditionallyRequired(field, []);

        const fieldValue = getFieldValue(field.id, []);

        const sectionLabel = field.sectionLabel || '';

        // Check field-level requirement
        if (isSchemaRequired || isAdminRequired || isConditionalRequired) {
          const isFilled = isFieldFilled(fieldValue, field.field_type);
          if (!isFilled) {
            missingFields.push({
              id: field.id,
              label: field.label || field.id,
              sectionLabel,
            });
          }
        }

        // Check option-level requirements for multi_select/single_select/yes_no fields
        if (
          field.options &&
          (field.field_type === 'multi_select' ||
            field.field_type === 'single_select' ||
            field.field_type === 'yes_no')
        ) {
          for (const option of field.options) {
            if (
              option.required_if &&
              isOptionConditionallyRequired(option, [])
            ) {
              // Check if this option is selected
              let isSelected = false;
              if (field.field_type === 'multi_select') {
                isSelected =
                  Array.isArray(fieldValue) && fieldValue.includes(option.id);
              } else if (field.field_type === 'yes_no') {
                // For yes_no, 'yes' option means fieldValue should be true, 'no' means false
                isSelected =
                  option.id === 'yes'
                    ? fieldValue === true
                    : fieldValue === false;
              } else {
                // single_select
                isSelected = fieldValue === option.id;
              }

              if (!isSelected) {
                missingFields.push({
                  id: field.id,
                  label: `${field.label || field.id} → ${option.label}`,
                  optionId: option.id,
                  sectionLabel,
                });
              }
            }
          }
        }
      }

      return missingFields;
    },
    [
      getAllFieldElements,
      isVisible,
      getFieldValue,
      isFieldFilled,
      isConditionallyRequired,
      isOptionConditionallyRequired,
    ],
  );

  /**
   * Check if all required fields are filled
   * @param {Object} formJson - The form schema
   * @param {string[]} requiredFields - Array of field IDs marked as required by admin
   * @returns {boolean} True if all visible required fields are filled
   */
  const areAllRequiredFieldsFilled = useCallback(
    (formJson, requiredFields = []) => {
      const missingFields = getMissingRequiredFields(formJson, requiredFields);
      return missingFields.length === 0;
    },
    [getMissingRequiredFields],
  );

  /**
   * Filter formData to only include entries from currently active instances.
   * This removes stale data from previous sessions where instances were deleted
   * or fields were cleared without triggering a save.
   * @param {Object} data - The formData to filter
   * @param {Object} instances - The current repeatingInstances state
   * @returns {Object} Filtered formData with only active instance entries
   */
  const filterFormDataByActiveInstances = useCallback((data, instances) => {
    if (!instances || Object.keys(instances).length === 0) {
      return data;
    }

    // Build set of all active instance prefixes (e.g., "mi_events_instance_2_1234567890")
    const activeInstancePrefixes = new Set();
    Object.values(instances).forEach((instanceIds) => {
      instanceIds.forEach((id) => activeInstancePrefixes.add(id));
    });

    // Filter formData
    const filtered = {};
    Object.entries(data).forEach(([key, value]) => {
      // Check if this is a repeating field key (contains instance pattern followed by __)
      const instanceMatch = key.match(/^(.+_instance_\d+_\d+)__/);
      if (instanceMatch) {
        // Only keep if the instance prefix is active
        if (activeInstancePrefixes.has(instanceMatch[1])) {
          filtered[key] = value;
        }
      } else {
        // Non-repeating field - always keep
        filtered[key] = value;
      }
    });

    return filtered;
  }, []);

  /**
   * Filter formData to exclude values for fields that are currently hidden.
   * @param {Object} data - The formData to filter
   * @param {Object} formJson - The form schema
   * @returns {Object} Filtered formData with only visible field values
   */
  const filterHiddenFieldValues = useCallback(
    (data, formJson) => {
      if (!formJson || !data) return data;

      // Track hidden repeating groups (treat their repeat_count as 0)
      const hiddenRepeatingGroups = new Set();
      const visibleFieldIds = new Set();

      // Custom getRepeatCount that returns 0 for hidden groups
      const getRepeatCountWithHidden = (fieldId) => {
        if (hiddenRepeatingGroups.has(fieldId)) return 0;
        return (repeatingInstances[fieldId] || []).length;
      };

      // Check visibility with hidden group handling
      const checkVisible = (visibleIf, instancePath = []) => {
        if (!visibleIf) return true;
        return evaluateCondition(
          visibleIf,
          (fieldId) => getFieldValue(fieldId, instancePath),
          getRepeatCountWithHidden,
        );
      };

      // First pass: identify hidden repeating groups
      const findHiddenRepeatingGroups = (elements) => {
        if (!Array.isArray(elements)) return;
        for (const element of elements) {
          if (!element) continue;
          if (element.type === 'group') {
            const groupVisible = isVisible(element.visible_if, []);
            if (element.repeating && !groupVisible) {
              hiddenRepeatingGroups.add(element.id);
            }
            if (element.elements) findHiddenRepeatingGroups(element.elements);
          }
          if (element.options) {
            for (const option of element.options) {
              if (option.child_elements)
                findHiddenRepeatingGroups(option.child_elements);
            }
          }
        }
      };

      formJson.sections?.forEach((section) => {
        if (section.elements) findHiddenRepeatingGroups(section.elements);
      });

      // Second pass: build visible field IDs
      const traverseElements = (elements, instancePath = []) => {
        if (!Array.isArray(elements)) return;
        for (const element of elements) {
          if (!element) continue;

          if (element.type === 'field') {
            const fieldVisible = checkVisible(element.visible_if, instancePath);
            if (fieldVisible) {
              visibleFieldIds.add(element.id);
              // Process child elements of selected options
              if (element.options) {
                for (const option of element.options) {
                  if (option.child_elements) {
                    const fieldValue = getFieldValue(element.id, instancePath);
                    const isSelected =
                      element.field_type === 'multi_select'
                        ? Array.isArray(fieldValue) &&
                          fieldValue.includes(option.id)
                        : fieldValue === option.id;
                    if (isSelected)
                      traverseElements(option.child_elements, instancePath);
                  }
                }
              }
            }
          } else if (element.type === 'group') {
            const groupVisible = checkVisible(element.visible_if, instancePath);
            if (groupVisible && element.elements) {
              if (element.repeating) {
                const instances = repeatingInstances[element.id] || [];
                for (const instanceId of instances) {
                  traverseElements(element.elements, [instanceId]);
                }
              } else {
                traverseElements(element.elements, instancePath);
              }
            }
          }
        }
      };

      formJson.sections?.forEach((section) => {
        if (section.elements) traverseElements(section.elements, []);
      });

      // Filter formData to only include visible fields
      const filtered = {};
      for (const [key, value] of Object.entries(data)) {
        if (key === COMMENTS_KEY || key.startsWith('_')) {
          filtered[key] = value;
          continue;
        }
        const parts = key.split('__');
        const baseFieldId = parts[parts.length - 1];
        if (visibleFieldIds.has(baseFieldId)) {
          filtered[key] = value;
        }
      }

      return filtered;
    },
    [isVisible, getFieldValue, repeatingInstances],
  );

  return {
    // State
    formData,
    setFormData,
    repeatingInstances,
    setRepeatingInstances,

    // Form Schema
    setFormJson,

    // Helpers
    generateInstanceId,
    buildFieldKey,
    getFieldValue,
    isVisible,
    isConditionallyRequired,
    isOptionConditionallyRequired,
    buildRenderWithGroupMap,
    getAllFieldElements,

    // Handlers (handleFieldChange returns { newlyRequired } for caller to handle)
    handleFieldChange,
    handleAddRepeatingInstance,
    handleRemoveRepeatingInstance,
    initializeRepeatingInstances,

    // Comment Management
    getFieldComments,
    getAllFieldComments,
    addComment,
    addReply,
    resolveComment,
    hasActiveComments,
    getAllActiveCommentFieldIds,
    getFieldCommentCount,
    getTotalCommentCount,

    // Suggestion Management
    getFieldSuggestions,
    getFieldSuggestionStats,
    getActiveFieldSuggestions,
    getAllSuggestionFieldIds,
    getAllActiveSuggestionFieldIds,
    getAllReviewFieldIds,
    getAllActiveReviewFieldIds,
    getFieldSuggestionCount,
    getTotalSuggestionStats,
    getTotalSuggestionCount,

    // Required Field Validation
    isFieldFilled,
    getMissingRequiredFields,
    areAllRequiredFieldsFilled,

    // Data Cleaning
    filterFormDataByActiveInstances,
    filterHiddenFieldValues,
  };
};

export default useDbqForm;
